import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import UserNotificationTypes from "@client.enums/userNotificationTypes";
import InvestmentTypes, { InvestmentScoreValue } from "@client.enums/investmentTypes";
import { makeNotificationSettings } from "./reducer";
import InsightSettingItem from "./InsightSettingItem";
import sparklesIcon from "@client.images/sparkles.svg";
import RecurringTypes from "@client.enums/recurringTypes";

const ActiveInsightsSection = ({ onSave, isNotificationDisabled }) => {
  const { t } = useTranslation();
  const notificationSettings = useSelector(makeNotificationSettings);

  const highInvestmentScore = useMemo(() => {
    const notification = notificationSettings.data.find(
      (n) => n.type === UserNotificationTypes.ScheduleHighInvestmentMeeting
    );
    return notification;
  }, [notificationSettings]);

  const aiPredictions = useMemo(() => {
    const notification = notificationSettings.data.find(
      (n) => n.type === UserNotificationTypes.AIPredictions
    );
    return notification;
  }, [notificationSettings]);

  const settingsConfig = [
    {
      notificationType: UserNotificationTypes.EmployeeOverspendInternal,
      titleKey: "components.Settings.InsightsSettings.internalMeeting",
      descriptionElements: [
        {
          type: "text",
          key: "components.Settings.InsightsSettings.employeeSpendsMoreThan",
        },
        {
          type: "input",
          isInput: true,
        },
        {
          type: "text",
          key: "components.Settings.InsightsSettings.hoursInInternalMeetings",
        },
        {
          type: "input",
          isInput: false,
        },
      ],
    },
    {
      notificationType: UserNotificationTypes.EmployeeOverspendExternal,
      titleKey: "components.Settings.InsightsSettings.externalMeeting",
      descriptionElements: [
        {
          type: "text",
          key: "components.Settings.InsightsSettings.employeeSpendsMoreThan",
        },
        {
          type: "input",
          isInput: true,
        },
        {
          type: "text",
          key: "components.Settings.InsightsSettings.hoursInExternalMeetings",
        },
        {
          type: "input",
          isInput: false,
        },
      ],
    },
    {
      notificationType: UserNotificationTypes.ScheduleRecurringMeeting,
      titleKey: "components.Settings.InsightsSettings.recurringMeeting",
      descriptionElements: [
        {
          type: "text",
          key: "components.Settings.InsightsSettings.employeeScheduled",
        },
        {
          type: "input",
          isInput: false,
        },
      ],
    },
    {
      notificationType: UserNotificationTypes.LowAttendance,
      titleKey: "components.Settings.InsightsSettings.lowAttendance",
      descriptionElements: [
        {
          type: "text",
          key: "components.Settings.InsightsSettings.lowAttendance.description1",
        },
        {
          type: "input",
          isInput: true,
          suffix: "%",
          inputClassName: "low-attendance-input",
        },
        {
          type: "text",
          key: "components.Settings.InsightsSettings.lowAttendance.description2",
        },
      ],
    },
    {
      notificationType: UserNotificationTypes.ScheduleOvercrowdedMeeting,
      titleKey: "components.Settings.InsightsSettings.noOfMeetingParticipants",
      descriptionElements: [
        {
          type: "text",
          key: "components.Settings.InsightsSettings.employeeScheduledMeetingWithMoreThan",
        },
        {
          type: "input",
          isInput: true,
        },
        {
          type: "text",
          key: "components.Settings.InsightsSettings.participants",
        },
      ],
    },
  ];

  // Special handling for high investment meeting setting
  const renderHighInvestmentSetting = () => {
    const baseDescriptionElements = [
      {
        type: "text",
        key: "components.Settings.InsightsSettings.employeeScheduledMeetingWith",
      },
      {
        type: "input",
        isInput: false,
      },
    ];

    // Add conditional elements based on investment type
    const conditionalElements = [];
    if (highInvestmentScore) {
      if (highInvestmentScore.settingsJson.investmentType === InvestmentTypes.Score) {
        conditionalElements.push({
          type: "dropdown",
          fieldKey: "investmentScoreValue",
          items: [
            { label: "Low", value: InvestmentScoreValue.Low },
            { label: "Medium", value: InvestmentScoreValue.Medium },
            { label: "High", value: InvestmentScoreValue.High },
          ],
        });
      } else if (highInvestmentScore.settingsJson.investmentType === InvestmentTypes.Amount) {
        conditionalElements.push({
          type: "input",
          isInput: true,
        });
      }
    }

    return (
      <InsightSettingItem
        key={UserNotificationTypes.ScheduleHighInvestmentMeeting}
        notificationType={UserNotificationTypes.ScheduleHighInvestmentMeeting}
        titleKey="components.Settings.InsightsSettings.highInvestmentMeeting"
        descriptionElements={[...baseDescriptionElements, ...conditionalElements]}
        onSave={onSave}
        isNotificationDisabled={isNotificationDisabled}
        specialConfig={highInvestmentScore}
      />
    );
  };

  const renderAIMeetingSimilaritiesSetting = () => {
    const descriptionElements = [
      {
        type: "text",
        key: "components.Settings.InsightsSettings.aiMeetingSimilaritiesRecognition.description",
      },
    ];

    return (
      <InsightSettingItem
        key={UserNotificationTypes.AIMeetingSimilaritiesRecognition}
        notificationType={UserNotificationTypes.AIMeetingSimilaritiesRecognition}
        titleKey="components.Settings.InsightsSettings.aiMeetingSimilaritiesRecognition"
        descriptionElements={descriptionElements}
        onSave={onSave}
        isNotificationDisabled={isNotificationDisabled}
        icon={sparklesIcon}
      />
    );
  };

  const renderAIPredictionsSetting = () => {
    const config = {
      titleKey: "components.Settings.InsightsSettings.aiPredictions",
      descriptionElements: [
        {
          type: "text",
          key: "components.Settings.InsightsSettings.aiPredictions.description",
        },
        {
          type: "dropdown",
          fieldKey: "type",
          items: [
            { label: t('components.Settings.InsightsSettings.PredictionType.week'), value: RecurringTypes.Weekly },
            { label: t('components.Settings.InsightsSettings.PredictionType.month'), value: RecurringTypes.Monthly },
            { label: t('components.Settings.InsightsSettings.PredictionType.quarter'), value: RecurringTypes.Quaterly },
          ],
        },
      ],
    }

    return (
      <InsightSettingItem
        key={UserNotificationTypes.AIPredictions}
        notificationType={UserNotificationTypes.AIPredictions}
        titleKey="components.Settings.InsightsSettings.aiPredictions"
        {...config}
        onSave={onSave}
        isNotificationDisabled={isNotificationDisabled}
        icon={sparklesIcon}
        specialConfig={aiPredictions}
      />
    );
  };

  return (
    <div className="settings-section">
      <span>
        {t("components.Settings.InsightsSettings.activeInsights")}
      </span>
      <div className="settings-list">
        {settingsConfig.map((config) => (
          <InsightSettingItem
            key={config.notificationType}
            {...config}
            onSave={onSave}
            isNotificationDisabled={isNotificationDisabled}
          />
        ))}
        {renderHighInvestmentSetting()}
        {renderAIMeetingSimilaritiesSetting()}
        {renderAIPredictionsSetting()}
      </div>
    </div>
  );
};

ActiveInsightsSection.propTypes = {
  onSave: PropTypes.func.isRequired,
  isNotificationDisabled: PropTypes.func.isRequired,
};

export default ActiveInsightsSection;
